import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { get } from 'lodash';
import moment from 'moment';
import { useEnhancedRouter } from '../../../router';
import { SLACK_SCOPES } from '../../../utils/constants';
import { useNotifier } from '../snackbar/store';
import { useTeams } from '../../api/teams';
import { useMe } from '../../api/me';
import { useSettings } from '../../api/settings';
import { noop } from '../../api/_utils';
import { useOrg } from '../../api/org';
import { Team } from '../teams/types';
import fromEntries from '../../../utils/fromEntries';

const slackIntegrationURL = ({ scopes = 'incoming-webhook', redirectURL = '', state = '' } = {}) => `https://slack.com/oauth/v2/authorize?scope=${scopes}&client_id=${process.env.NEXT_PUBLIC_SLACK_CLIENT_ID}&redirect_uri=${redirectURL}&state=${state}`;

type EnrichIntegrationObject = (a: { teams: Team[] }) => { [key: string]: any }
const enrichIntegrationObject: EnrichIntegrationObject = ({ teams = [] }) => (obj) => {
  if (!obj || !teams) return;
  const getTeamName = (teamID) => get(teams.find((t: any) => t.id === teamID), 'name', '');
  // eslint-disable-next-line consistent-return
  return {
    ...obj,
    teamName: getTeamName(obj.teamID) || 'All teams',
  };
};

export const useSettingsPageIntegrationsStore = () => {
  const router = useEnhancedRouter();
  const { me } = useMe();

  const {
    integrations,
    postCodeToFinishIntegration,
    loading: loadingIntegrations,
    slackChannels,
    toggleSpecialSettings,
    onSlackDisconnect: _onSlackDisconnect,
  } = useSettings({ channels: true });

  const { teams, loading: loadingTeams } = useTeams();
  const state = 'BOT';
  const [redirectURL, setRedirectURL] = useState('');
  const { bot: botScope } = SLACK_SCOPES;
  const { enqueueNotification } = useNotifier();

  // this useEffect ensures presence of 'window'
  useEffect(() => {
    // eslint-disable-next-line no-undef
    setRedirectURL(window.location.origin + window.location.pathname);
  }, []);

  useEffect(() => {
    if (get(me, 'isAdmin') && redirectURL) {
      (async function effect() {
        // eslint-disable-next-line no-shadow
        const { query: { code, state } } = router;
        if (code) {
          const c: string = Array.isArray(code) ? code.join('') : code || '';
          const s: string = Array.isArray(state) ? state.join('') : state || '';
          try {
            await postCodeToFinishIntegration({ code: c, redirectURL, state: s });
            // remove the ?code=...&state=... params from URL
            router.replace(window.location.pathname);
            enqueueNotification('Connected to Slack!');
            return;
          } catch (e) {
            // remove the ?code=...&state=... params from URL
            router.replace(window.location.pathname);
          }
        }
      }());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, redirectURL]);

  const initiateSummaryIntegrationURL = slackIntegrationURL({
    scopes: botScope,
    state,
    redirectURL,
  });
  const initiateBotIntegrationURL = slackIntegrationURL({
    scopes: botScope,
    redirectURL: redirectURL || '',
    state,
  });

  const safeToggleSettings = async (a) => {
    try {
      await toggleSpecialSettings(a);
    } catch (e) {
      noop();
    }
  };

  const onSlackDisconnect = () => _onSlackDisconnect().then(() => {
    enqueueNotification('Disconnected from Slack!');
  }).catch(noop);

  return useMemo(() => ({
    done: !loadingIntegrations && !loadingTeams,
    integrations: {
      ...integrations,
      slack: {
        ...(get(integrations, 'slack', {})),
        summaries: get(integrations, 'slack.summaries', []).map(enrichIntegrationObject({ teams })),
      },
    },
    me,
    initiateSummaryIntegrationURL,
    initiateBotIntegrationURL,
    slackChannels: (slackChannels || []).map((channel) => ({
      ...channel,
      label: channel.name || channel.label,
      value: channel.id || channel.value,
      isGeneral: channel.general || false,
    })),
    toggleSpecialSettings: safeToggleSettings,
    onSlackDisconnect,
  }), [
    loadingTeams,
    loadingIntegrations,
    integrations,
    me,
    initiateSummaryIntegrationURL,
    initiateBotIntegrationURL,
    teams,
    slackChannels,
  ]);
};

export const SettingsPageIntegrationsStoreWrapper = ({ children }) => {
  const storeData = useSettingsPageIntegrationsStore();

  return children(storeData);
};

export const useEditSlackIntegrationStore = () => {
  const router = useEnhancedRouter();
  const {
    integrations,
    loading: loadingIntegrations,
    editSlackIntegration,
    slackChannels,
    deleteSlackIntegration,
    createSlackIntegration,
  } = useSettings();

  const { enqueueNotification } = useNotifier();

  const { teams } = useTeams();

  const currentIntegration = useMemo(() => get(integrations, 'slack.summaries', [])
    .find((i) => i.id === get(router, 'query.id')),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [integrations, router.query.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onClose = useCallback(() => router.endJourney(), []);

  const onCreateSlackIntegration = (values) => {
    const getCoverage = (teamID) => (teamID === 'all' ? 'ENTIRE_ORG' : 'SPECIFIC_TEAM');
    const data = {
      ...values,
      type: get(router, 'query.type'),
      coverage: getCoverage(values.teamID),
      teamID: values.teamID === 'all' ? null : values.teamID,
    };
    return createSlackIntegration(data).then(() => {
      enqueueNotification('New summary notification added!');
      onClose();
    }).catch(noop);
  };

  const onEditIntegration = (values) => {
    const getCoverage = (teamID) => (teamID === 'all' ? 'ENTIRE_ORG' : 'SPECIFIC_TEAM');
    const data = {
      ...values,
      coverage: getCoverage(values.teamID),
      teamID: values.teamID === 'all' ? null : values.teamID,
    };
    return editSlackIntegration(data)
      .then(() => {
        enqueueNotification('Saved!');
        onClose();
      }).catch(noop);
  };

  const onDeleteSlackIntegration = ({ id, type, deprecated }) => deleteSlackIntegration({
    id,
    type,
    deprecated,
  })
    .then(() => {
      enqueueNotification('Removed!');
      onClose();
    }).catch(noop);

  const type = get(router, 'query.type');

  const slackChannels_ = useMemo(() => (slackChannels || []).filter((c) => c.isVisibleToUser), [slackChannels]);

  return useMemo(() => ({
    onClose,
    done: !loadingIntegrations,
    teams,
    slackChannels: slackChannels_,
    integration: currentIntegration,
    onEditIntegration,
    onDeleteSlackIntegration,
    onCreateSlackIntegration,
    type,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    loadingIntegrations, teams,
    onClose,
    slackChannels,
    onCreateSlackIntegration,
    currentIntegration,
    onEditIntegration,
    type,
  ]);
};

export const EditSlackIntegrationStoreWrapper = ({ children }) => {
  const storeData = useEditSlackIntegrationStore();

  return children(storeData);
};

type Cycle = { startDate: string, endDate: string }
type MapObject = <A, B>(fn: (a: A) => B) => (obj: {[key: string]: A }) => { [key: string]: B}
export const mapObject: MapObject = (fn: (any) => any) => (obj) => {
  const kvPairs = Object.entries(obj);
  const mapped = kvPairs.map((kvPair): [string, any] => [kvPair[0], fn(kvPair[1])]);
  return fromEntries(mapped);
};

type UseReports = {
  options: {startDate: string, endDate: string}[],
  onDownloadReport: (a: {
    startDate: string,
    endDate: string
  }) => any,
  onDownloadLeavesRecord: (a:{
    startDate: string,
    endDate: string,
    includeArchived?: boolean,
  }) => Promise<any>,
  orgName: string,
}
export const useReports = (): UseReports => {
  const { org } = useOrg();
  const { exportData, exportLeavesRecord } = useSettings();
  const options = get(org, 'cycles', []).map(mapObject((x: string) => moment(x).format('YYYY-MM-DD')));
  const onDownloadReport = (a) => exportData(a).catch(noop);
  const onDownloadLeavesRecord = (a: {
    startDate: string,
    endDate: string,
    includeArchived?: boolean,
  }) => exportLeavesRecord(a).catch(noop);
  return {
    options: options
      .sort((a, b) => (a.endDate < b.endDate ? -1 : 1))
      .map((a: Cycle, idx: number) => {
        const isCurrentCycle = moment().isBetween(a.startDate, a.endDate, undefined, '[]');
        const startFormat = moment(a.startDate).format('YYYY') === moment(a.endDate).format('YYYY') ? 'MMMM' : 'MMMM YYYY';
        return ({
          label: `${moment(a.startDate).format(startFormat)} - ${moment(a.endDate).format('MMMM YYYY')}${isCurrentCycle ? ' (Current cycle)' : ''}`,
          id: idx,
          startDate: a.startDate,
          endDate: a.endDate,
          isCurrentCycle,
        });
      }),
    onDownloadReport,
    onDownloadLeavesRecord,
    orgName: get(org, 'name', ''),
  };
};
